@if (!isVerifyPaymentLoading && !isPaymentSuccess) {
<section class="plan-update-dialog">
  <header class="plan-update-dialog__header">
    <div class="plan-update-dialog__heading">
      <span class="plan-update-dialog__eyebrow">SUBSCRIPTION</span>
      @if (!isSubscriptionUpdated) {
      <h2>Change your plan</h2>
      <p>Adjust your subscription and confirm the billing information.</p>
      } @else {
      <h2>Subscription updated</h2>
      <p>Your new subscription details are ready.</p>
      }
    </div>

    @if (!isSubscriptionExpired()) {
    <button mat-icon-button class="plan-update-dialog__close" type="button" aria-label="Close dialog"
      (click)="closeDialog()">
      <mat-icon>close</mat-icon>
    </button>
    }
  </header>

  @if (!isSubscriptionUpdated) {
  <div class="plan-update-steps" aria-label="Plan update progress">
    <button type="button" class="plan-update-step" [class.plan-update-step--active]="selectedTab === 'tab1'"
      [class.plan-update-step--complete]="selectedTab === 'tab2'" (click)="selectedTab='tab1'">
      <span class="plan-update-step__number">1</span>
      <span>
        <strong>Plan details</strong>
        <small>Choose billing and capacity</small>
      </span>
    </button>
    <span class="plan-update-steps__connector"
      [class.plan-update-steps__connector--complete]="selectedTab === 'tab2'"></span>
    <button type="button" class="plan-update-step" [class.plan-update-step--active]="selectedTab === 'tab2'"
      (click)="selectedTab='tab2'">
      <span class="plan-update-step__number">2</span>
      <span>
        <strong>Billing address</strong>
        <small>Confirm payment details</small>
      </span>
    </button>
  </div>

  <form class="plan-update-form" [formGroup]="editBillingAddressForm" (ngSubmit)="onFormSubmit()">
    @if (selectedTab === 'tab1') {
    <div class="plan-update-dialog__body plan-update-dialog__body--plan">
      <section class="plan-selection" aria-labelledby="plan-selection-title">
        <div class="section-heading">
          <span class="section-heading__icon"><mat-icon>tune</mat-icon></span>
          <span>
            <h3 id="plan-selection-title">Configure your plan</h3>
            <p>Select a billing cycle and the screens you need.</p>
          </span>
        </div>

        <mat-radio-group class="billing-cycle" [(ngModel)]="selectedPlan" [ngModelOptions]="{standalone: true}"
          (change)="onPlanOrScreenChange()">
          <mat-radio-button value="yearly" class="billing-cycle__option">
            <span class="billing-cycle__content">
              <span class="billing-cycle__title-row">
                <strong>Yearly</strong>
                <span class="billing-cycle__saving">Best value</span>
              </span>
              <span class="billing-cycle__price">{{ getYearlyTotalAmount() | currency:'INR'
                }}<small>/month</small></span>
              <span class="billing-cycle__caption">Save {{ getSaveAmount() | currency:'INR' }} per year</span>
            </span>
          </mat-radio-button>

          <mat-radio-button value="monthly" class="billing-cycle__option">
            <span class="billing-cycle__content">
              <span class="billing-cycle__title-row"><strong>Monthly</strong></span>
              <span class="billing-cycle__price">{{ getMonthlyTotalAmount() | currency:'INR'
                }}<small>/month</small></span>
              <span class="billing-cycle__caption">Flexible monthly billing</span>
            </span>
          </mat-radio-button>
        </mat-radio-group>

        <div class="screen-allocation">
          <div class="screen-allocation__header">
            <span>
              <strong>Screen allocation</strong>
              <small>Choose between {{ minScreens }} and {{ maxScreens }} screens.</small>
            </span>
            <label class="screen-count">
              <input matInput type="number" [min]="0" [max]="maxScreens" [(ngModel)]="numberOfScreens"
                [ngModelOptions]="{standalone: true}" (input)="sanitizeScreenInput($event)"
                #numberOfScreensInput="ngModel" required aria-label="Number of screens" />
              <span>screens</span>
            </label>
          </div>

          <input type="range" class="custom-slider" [(ngModel)]="numberOfScreens" [ngModelOptions]="{standalone: true}"
            (input)="onPlanOrScreenChange()" [min]="minScreens" [max]="maxScreens" aria-label="Screen allocation" />
          <div class="screen-allocation__range">
            <span>{{ minScreens }}</span>
            <span>{{ maxScreens }}</span>
          </div>
        </div>

        <div class="invoice-note">
          <mat-icon>mail_outline</mat-icon>
          <span>Prefer to pay by invoice? Contact
            <a href="mailto:business&#64;zignflix.com">business&#64;zignflix.com</a>.
          </span>
        </div>
      </section>

      <aside class="plan-summary" aria-labelledby="plan-summary-title">
        <div class="plan-summary__header">
          <span>
            <span class="plan-summary__eyebrow">ORDER SUMMARY</span>
            <h3 id="plan-summary-title">Your updated plan</h3>
          </span>
          <span class="plan-summary__cycle">{{ selectedPlan === 'yearly' ? 'Yearly' : 'Monthly' }}</span>
        </div>

        <div class="plan-summary__metrics">
          <div><mat-icon>desktop_windows</mat-icon><span><small>Screens</small><strong>{{ planSummary.screens
                }}</strong></span></div>
          <div><mat-icon>group</mat-icon><span><small>Users</small><strong>{{ planSummary.users }}</strong></span></div>
          <div><mat-icon>cloud_queue</mat-icon><span><small>Storage</small><strong>{{ planSummary.storage | bytesToGb
                }}</strong></span></div>
        </div>

        <dl class="plan-summary__breakdown">
          <div>
            <dt>Plan validity</dt>
            <dd>{{ planSummary.validityDays }} days</dd>
          </div>
          <div>
            <dt>Rate per screen</dt>
            <dd>{{ planSummary.perScreenRate | currency:'INR' }}</dd>
          </div>
          <div>
            <dt>Plan total</dt>
            <dd>{{ planSummary.totalAmount | currency:'INR' }}</dd>
          </div>
          <div>
            <dt>Discount ({{ selectedPlan === 'yearly' ? yearlyDiscountPercentage * 100 : 0 }}%)</dt>
            <dd class="plan-summary__deduction">- {{ (selectedPlan === 'yearly' ? getSaveAmount() : 0) | currency:'INR'
              }}</dd>
          </div>
          @if (billingAction === 'upgrade') {
          <div>
            <dt>New plan cost for remaining days</dt>
            <dd>{{ proratedPlanAmount | currency:'INR' }}</dd>
          </div>
          }
          @if (planSummary.unconsumedAmount > 0) {
          <div>
            <dt class="plan-summary__info">Credit reallocation
              <mat-icon matTooltip="Adjusting the amount from your existing plan.">info_outline</mat-icon>
            </dt>
            <dd class="plan-summary__deduction">- {{ planSummary.unconsumedAmount | currency:'INR' }}</dd>
          </div>
          }
          <div>
            <dt>Subtotal</dt>
            <dd>{{ planSummary.totalAmountAdjusted | currency:'INR' }}</dd>
          </div>
          <div>
            <dt>GST (18%)</dt>
            <dd>{{ planSummary.gstAmount | currency:'INR' }}</dd>
          </div>
        </dl>

        <div class="plan-summary__validity">
          <span>Valid up to</span>
          <strong>{{ getNextPaymentDate() | date:dateFormat }}</strong>
        </div>
        @if (billingAction === 'renewal') {
        <p class="dialog-subtitle">Adds {{ selectedPlan === 'yearly' ? 365 : 30 }} days after your current expiry.</p>
        } @else if (billingAction === 'upgrade') {
        <p class="dialog-subtitle">Pay only the difference for the remaining period. Your expiry stays the same.</p>
        } @else if (billingAction === 'reallocation') {
        <p class="dialog-subtitle">Your remaining paid value is converted into validity at the updated plan rate.</p>
        }
        <div class="plan-summary__total">
          <span>Total amount</span>
          <strong>{{ planSummary.totalAmountPayable | currency:'INR' }}</strong>
        </div>
      </aside>
    </div>
    }

    @if (selectedTab === 'tab2') {
    <div class="plan-update-dialog__body plan-update-dialog__body--billing" [style.height.px]="getFormHeight()">
      <div class="section-heading">
        <span class="section-heading__icon"><mat-icon>location_on</mat-icon></span>
        <span>
          <h3>Billing address</h3>
          <p>Confirm where your invoices and payment records should be addressed.</p>
        </span>
      </div>

      <div class="billing-form-grid">
        <mat-form-field appearance="outline" class="billing-form-grid__wide">
          <mat-label>Full Name</mat-label>
          <input matInput formControlName="full_name" placeholder="Enter full name" [attr.maxLength]="limits.name">
          @if (editBillingAddressForm.get('full_name')?.hasError('required')) { <mat-error>Full name is
            required</mat-error> }
        </mat-form-field>

        <mat-form-field appearance="outline" class="billing-form-grid__wide">
          <mat-label>Address Line 1</mat-label>
          <input matInput formControlName="address_1" placeholder="Enter address line 1"
            [attr.maxLength]="limits.address">
          @if (editBillingAddressForm.get('address_1')?.hasError('required')) { <mat-error>Address Line 1 is
            required</mat-error> }
        </mat-form-field>

        <mat-form-field appearance="outline" class="billing-form-grid__wide">
          <mat-label>Address Line 2</mat-label>
          <input matInput formControlName="address_2" placeholder="Enter address line 2"
            [attr.maxLength]="limits.address">
          @if (editBillingAddressForm.get('address_2')?.hasError('required')) { <mat-error>Address Line 2 is
            required</mat-error> }
        </mat-form-field>

        <mat-form-field appearance="outline">
          <mat-label>Country</mat-label>
          <mat-select formControlName="country" placeholder="Select country">
            <mat-option value="">Select</mat-option>
            @for (country of countries; track country) { <mat-option [value]="country.id">{{ country.name
              }}</mat-option> }
          </mat-select>
          @if (editBillingAddressForm.get('country')?.hasError('required')) { <mat-error>Country is required</mat-error>
          }
        </mat-form-field>

        <mat-form-field appearance="outline">
          <mat-label>State</mat-label>
          <mat-select formControlName="state" placeholder="Select state" [disabled]="states.length === 0">
            <mat-option value="">Select</mat-option>
            @for (state of states; track state) { <mat-option [value]="state.id">{{ state.name }}</mat-option> }
          </mat-select>
          @if (editBillingAddressForm.get('state')?.hasError('required')) { <mat-error>State is required</mat-error> }
        </mat-form-field>

        <mat-form-field appearance="outline">
          <mat-label>City</mat-label>
          <input matInput formControlName="city" placeholder="Enter city" [attr.maxLength]="limits.name">
          @if (editBillingAddressForm.get('city')?.hasError('required')) { <mat-error>City is required</mat-error> }
        </mat-form-field>

        <mat-form-field appearance="outline">
          <mat-label>Zip Code</mat-label>
          <input matInput formControlName="zip_code" placeholder="Enter zip code" [attr.maxLength]="limits.name">
          @if (editBillingAddressForm.get('zip_code')?.hasError('required')) { <mat-error>Zip code is
            required</mat-error> }
          @if (editBillingAddressForm.get('zip_code')?.hasError('pattern')) { <mat-error>Invalid zip code
            format</mat-error> }
        </mat-form-field>

        <mat-form-field appearance="outline" class="billing-form-grid__wide">
          <mat-label>Mobile Phone</mat-label>
          <input matInput formControlName="mobile_number" [attr.maxLength]="limits.name">
          @if (editBillingAddressForm.get('mobile_number')?.hasError('pattern')) { <mat-error>Invalid mobile phone
            format</mat-error> }
        </mat-form-field>
      </div>
    </div>
    }

    <footer class="plan-update-dialog__footer">
      @if (selectedTab === 'tab1') {
      <button mat-flat-button class="secondary-btn" type="button" (click)="closeDialog()">Exit</button>
      <button mat-flat-button class="primary-btn" type="button" (click)="selectedTab='tab2'">Next</button>
      }
      @if (selectedTab === 'tab2') {
      <button mat-flat-button class="secondary-btn" type="button" (click)="selectedTab='tab1'">Back</button>
      <button mat-flat-button class="primary-btn" type="submit">Proceed to payment</button>
      }
    </footer>
  </form>
  } @else {
  <div class="subscription-result">
    <span class="subscription-result__icon"><mat-icon>check</mat-icon></span>
    <h3>Plan updated successfully</h3>
    <p>{{ subscriptionUpdatedMessage }}</p>
    <button mat-flat-button class="primary-btn" type="button" (click)="closeDialog()">Continue</button>
  </div>
  }
</section>
}

@if (isVerifyPaymentLoading && !isPaymentSuccess) {
<section class="payment-status" aria-live="polite">
  <div class="zign-spinner"></div>
  <h2>Verifying your payment</h2>
  <p>Please keep this window open while we confirm the transaction.</p>
</section>
}

@if (isPaymentSuccess) {
<section class="payment-status payment-status--success" aria-live="polite">
  <span class="payment-status__icon"><mat-icon>check</mat-icon></span>
  <h2>Payment verified</h2>
  <p>Your plan has been updated successfully.</p>
  <span class="payment-status__countdown">Refreshing in <strong>{{ reloadCounter }}</strong> seconds</span>
</section>
}
